"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, PiggyBank, SlidersHorizontal, Zap, ChartBar as BarChart3, ShieldCheck, ArrowRight, Check, X } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const benefits = [{
  icon: UserCheck,
  title: "100% Direct Senior Access",
  highlight: "No Account Managers",
  description: "When you hire me, you work directly with Yassine Essaadi. There are no inexperienced junior account managers or layered delegation chains standing between you and your SEO strategy."
}, {
  icon: PiggyBank,
  title: "Cost Efficiency & true Value",
  highlight: "Zero Corporate Overhead",
  description: "Agencies charge high monthly retainers to fund plush offices, sales teams, and administrative bloat. With my consultancy, every pound invested goes straight into high-impact SEO work."
}, {
  icon: SlidersHorizontal,
  title: "Tailored SEO Strategy",
  highlight: "No Cookie-Cutter Checklists",
  description: "I do not apply rigid 50-point agency templates across 30 clients. I craft bespoke, data-driven strategies designed specifically for your target audience, platform, and commercial goals."
}, {
  icon: Zap,
  title: "Speed of Execution",
  highlight: "Agile Implementation",
  description: "Agencies can take weeks to review and approve simple technical tweaks. I act fast, communicating directly with your developers or executing changes myself to accelerate your organic growth."
}, {
  icon: BarChart3,
  title: "Transparent & Honest Reporting",
  highlight: "Revenue Over Vanity Metrics",
  description: "I measure success through real business outcome—qualified leads, pipeline value, and sustainable organic traffic growth—rather than fluff reports filled with meaningless keyword counts."
}, {
  icon: ShieldCheck,
  title: "Personal Accountability",
  highlight: "Single Point of Expertise",
  description: "My reputation as an independent SEO consultant rests on delivering real results. I take personal ownership of your search visibility, maintaining clear and direct communication throughout."
}];
const comparisonRows = [{
  feature: "Who performs the actual work?",
  consultant: "Yassine Essaadi (Senior SEO Consultant)",
  agency: "Often passed to junior executives or interns"
}, {
  feature: "Strategy customization",
  consultant: "100% bespoke based on your business model",
  agency: "Standardized agency templates & checklists"
}, {
  feature: "Response time & communication",
  consultant: "Direct phone, email & Slack access",
  agency: "Filtered through an account manager"
}, {
  feature: "Fee allocation",
  consultant: "Entirely dedicated to strategy & execution",
  agency: "High percentage covers agency overhead"
}, {
  feature: "Client roster focus",
  consultant: "Select group of businesses for deep focus",
  agency: "High volume of accounts per team member"
}];
export default function Consultant() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-sm font-medium bg-secondary text-secondary-foreground border border-accent/20">
            Why Work Directly With Me
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-6">
            Independent SEO Consultant vs Traditional Agency
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-default">
            When you partner with YE Consultancy, you choose personal dedication and senior expertise over agency overhead, delayed feedback loops, and automated client management.
          </p>
        </div>

        {/* Benefits Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-20">
          {benefits.map((benefit, i) => {
          const IconComponent = benefit.icon;
          return <motion.div key={benefit.title} variants={itemVariants} data-index={i}>
                <Card className="h-full bg-card text-card-foreground border border-border/80 shadow-xs hover:border-accent/40 transition-[border-color,box-shadow] duration-300 rounded-xl flex flex-col justify-between">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-lg bg-primary/10 text-primary">
                        <IconComponent className="size-6" />
                      </div>
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground border border-accent/20">
                        {benefit.highlight}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-semibold text-foreground font-default">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-muted-foreground text-sm leading-relaxed font-default">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Comparison Table */}
        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 lg:p-10 shadow-xs mb-16">
          <div className="max-w-3xl mb-8">
            <h3 className="text-2xl sm:text-3xl font-semibold text-foreground font-default mb-3">
              Clear Comparison: What Changes When You Work With Me
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base font-default">
              Here is how my hands-on consulting model directly contrasts with standard agency retainer models.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-4 px-4 text-sm font-semibold text-foreground w-1/3">Key Factors</th>
                  <th className="py-4 px-4 text-sm font-semibold text-primary bg-secondary/50 rounded-t-lg w-1/3">
                    <div className="flex items-center gap-2">
                      <Check className="size-4 text-primary" />
                      <span>YE Consultancy (Yassine)</span>
                    </div>
                  </th>
                  <th className="py-4 px-4 text-sm font-semibold text-muted-foreground w-1/3">
                    <div className="flex items-center gap-2">
                      <X className="size-4 text-destructive" />
                      <span>Standard SEO Agency</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {comparisonRows.map((row, i) => <tr key={row.feature} data-index={i} className="hover:bg-muted/30 transition-colors">
                    <td className="py-4 px-4 text-sm font-medium text-foreground">
                      {row.feature}
                    </td>
                    <td className="py-4 px-4 text-sm text-foreground bg-secondary/20 font-medium">
                      {row.consultant}
                    </td>
                    <td className="py-4 px-4 text-sm text-muted-foreground">
                      {row.agency}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="bg-muted/60 border border-border rounded-2xl p-8 sm:p-12 text-center max-w-4xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-semibold text-foreground font-default mb-4">
            Ready for Senior-Level SEO Leadership?
          </h3>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto mb-8 font-default">
            Let us discuss your website search performance, market opportunities, and tailored SEO growth strategy directly.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-8">
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}